import React, { useCallback } from 'react';

import { Country } from '@pushwoosh/geo-ip';
import { ReCAPTCHA, useRecaptcha } from '~/src/components/recaptcha';
import {
  HomeSVG,
  EmailSVG,
  UserSVG,
  StatsSVG,
  PhoneSVG,
  WebsiteSVG,
  IndustrySVG,
} from '~/src/assets/icons';
import { Button } from '~/src/ui/button';
import { Checkbox } from '~/src/ui/checkbox';
import { ErrorMessage } from '~/src/ui/error-message';
import { Link } from '~/src/ui/link';
import { Select } from '~/src/ui/select';
import { TextInput } from '~/src/ui/text-input';
import { Footnote } from '~/src/ui/typography';
import { sendToGoogleTagManager } from '~/src/helpers/sendToGoogleTagManager';

import { FormProps, FormName } from './form.types';
import { FormBox, FormField, FormGroup } from './form.styles';

const industries = [
  { label: 'Airlines', value: 'Airlines' },
  { label: 'Banking', value: 'Banking' },
  { label: 'Computer Software', value: 'Computer Software' },
  { label: 'E-commerce', value: 'E-commerce' },
  { label: 'Education', value: 'Education' },
  { label: 'Finance', value: 'Finance' },
  { label: 'Fitness', value: 'Fitness' },
  { label: 'Food & Beverages', value: 'Food & Beverages' },
  { label: 'Gambling', value: 'Gambling' },
  { label: 'Games', value: 'Games' },
  { label: 'Healthcare', value: 'Healthcare' },
  { label: 'Hospitality and Real Estate', value: 'Hospitality and Real Estate' },
  { label: 'Information Technology & Services', value: 'Information Technology & Services' },
  { label: 'Insurance', value: 'Insurance' },
  { label: 'Internet', value: 'Internet' },
  { label: 'Marketing & Advertising & Consulting', value: 'Marketing & Advertising & Consulting' },
  { label: 'Media / Entertainment', value: 'Media / Entertainment' },
  { label: 'Mobile apps', value: 'Mobile apps' },
  { label: 'Mobility & Delivery', value: 'Mobility & Delivery' },
  { label: 'Non-profit', value: 'Non-profit' },
  { label: 'Sports', value: 'Sports' },
  { label: 'Telecommunications', value: 'Telecommunications' },
  { label: 'Transportation', value: 'Transportation' },
  { label: 'Other', value: 'Other' },
];

const mau = [
  { label: 'Pre-launch', value: 'Pre-launch' },
  { label: 'Under 10k', value: 'Under 10k' },
  { label: '10k - 50k', value: '10k - 50k' },
  { label: '50k - 100k', value: '50k - 100k' },
  { label: '100k - 500k', value: '100k - 500k' },
  { label: '500k - 1M', value: '500k - 1M' },
  { label: '1M+', value: '1M+' },
];

const countries: Array<{ value: string, label: string }> = Object.entries(Country).map(([key, country]) => ({ value: key, label: country }));
// added country without iso
countries.push({ label: 'West Bank and Gaza', value: 'West Bank and Gaza' });

export function Form(props: FormProps): JSX.Element {
  const { getRecaptchaToken } = useRecaptcha();
  const {
    buttonText,
    footnoteText,
    enablePartnerPrivacy,
    partnerPrivacyLinkList,
    enableMau,
    mixpanelAttribute,
    metricEvent,
    source,
    form,
    hidePhone,
  } = props;

  const onSubmitClick = useCallback(
    async (event) => {
      event.preventDefault();
      const token = await getRecaptchaToken();

      form.setFieldValue(FormName.RECAPTCHA_RESPONSE, token);
      form.submitForm();

      if (metricEvent) {
        sendToGoogleTagManager('event', metricEvent);
      }
    },
    [form, metricEvent, getRecaptchaToken],
  );

  const onPhoneChange = useCallback(
    ({ name, value }) => {
      const numericValue = value.replace(/\D/g, '');
      const plusValue = numericValue && numericValue.replace(/^/, '+');

      form.setFieldValue(name, plusValue.replace(/(\d{1})(\d{3})(\d{3})(\d{4})/, '$1 ($2) $3 $4').trim());
    },
    [form],
  );

  return (
    <FormBox id={`form-id-${source}`}>
      <FormGroup>
        {/* FIRST NAME */}
        <div>
          <FormField>
            <TextInput
              name={FormName.FIRST_NAME}
              zohoInputName="firstname"
              value={form.values[FormName.FIRST_NAME]}
              placeholder="First Name…"
              icon={(
                <UserSVG />
              )}
              isErrored={form.touched[FormName.FIRST_NAME] && !!form.errors[FormName.FIRST_NAME]}
              isDisabled={form.isSubmitting}
              onChange={({ name, value }): void => form.setFieldValue(name, value)}
              onBlur={({ name }): void => form.setFieldTouched(name)}
            />
            {form.touched[FormName.FIRST_NAME] && (
              <ErrorMessage message={form.errors[FormName.FIRST_NAME]} />
            )}
          </FormField>
        </div>

        {/* LAST NAME */}
        <div>
          <FormField>
            <TextInput
              name={FormName.LAST_NAME}
              zohoInputName="lastname"
              value={form.values[FormName.LAST_NAME]}
              placeholder="Last Name…"
              icon={(
                <UserSVG />
              )}
              isErrored={form.touched[FormName.LAST_NAME] && !!form.errors[FormName.LAST_NAME]}
              isDisabled={form.isSubmitting}
              onChange={({ name, value }): void => form.setFieldValue(name, value)}
              onBlur={({ name }): void => form.setFieldTouched(name)}
            />
            {form.touched[FormName.LAST_NAME] && (
              <ErrorMessage message={form.errors[FormName.LAST_NAME]} />
            )}
          </FormField>
        </div>
      </FormGroup>

      {/* EMAIL */}
      <FormField>
        <TextInput
          name={FormName.EMAIL}
          zohoInputName="email"
          value={form.values[FormName.EMAIL]}
          placeholder="Business Email…"
          icon={(
            <EmailSVG />
          )}
          isErrored={form.touched[FormName.EMAIL] && !!form.errors[FormName.EMAIL]}
          isDisabled={form.isSubmitting}
          onChange={({ name, value }): void => form.setFieldValue(name, value.trim())}
          onBlur={({ name }): void => form.setFieldTouched(name)}
        />
        {form.touched[FormName.EMAIL] && (
          <ErrorMessage
            message={form.errors[FormName.EMAIL]}
            mixpanelAttribute={mixpanelAttribute}
          />
        )}
      </FormField>

      {/* PHONE */}
      {!hidePhone && (
        <FormField>
          <TextInput
            name={FormName.PHONE}
            zohoInputName="phone"
            value={form.values[FormName.PHONE]}
            placeholder="Phone Number…"
            icon={(
              <PhoneSVG />
            )}
            type="tel"
            maxLength={13}
            isErrored={form.touched[FormName.PHONE] && !!form.errors[FormName.PHONE]}
            isDisabled={form.isSubmitting}
            onChange={onPhoneChange}
            onBlur={({ name }): void => form.setFieldTouched(name)}
          />
          {form.touched[FormName.PHONE] && (
            <ErrorMessage
              message={form.errors[FormName.PHONE]}
            />
          )}
        </FormField>
      )}

      {/* COUNTRY */}
      <FormField>
        <Select
          name={FormName.COUNTRY}
          value={form.values[FormName.COUNTRY]}
          placeholder="Country…"
          icon={(
            <HomeSVG />
          )}
          options={countries}
          isErrored={form.touched[FormName.COUNTRY] && !!form.errors[FormName.COUNTRY]}
          isDisabled={form.isSubmitting}
          onChange={({ name, value }): void => form.setFieldValue(name, value)}
          onBlur={({ name }): void => form.setFieldTouched(name)}
        />
        {form.touched[FormName.COUNTRY] && (
          <ErrorMessage message={form.errors[FormName.COUNTRY]} />
        )}
      </FormField>

      {/* COMPANY / WEBSITE */}
      <FormField>
        <TextInput
          name={FormName.COMPANY}
          zohoInputName="lastname"
          value={form.values[FormName.COMPANY]}
          placeholder="Company / Website..."
          icon={(
            <WebsiteSVG />
          )}
          isErrored={form.touched[FormName.COMPANY] && !!form.errors[FormName.COMPANY]}
          isDisabled={form.isSubmitting}
          onChange={({ name, value }): void => form.setFieldValue(name, value)}
          onBlur={({ name }): void => form.setFieldTouched(name)}
        />
        {form.touched[FormName.COMPANY] && (
          <ErrorMessage message={form.errors[FormName.COMPANY]} />
        )}
      </FormField>

      {/* INDUSTRY */}
      <FormField>
        <Select
          name={FormName.INDUSTRY}
          value={form.values[FormName.INDUSTRY]}
          placeholder="Industry…"
          icon={(
            <IndustrySVG />
          )}
          options={industries}
          isErrored={form.touched[FormName.INDUSTRY] && !!form.errors[FormName.INDUSTRY]}
          isDisabled={form.isSubmitting}
          onChange={({ name, value }): void => form.setFieldValue(name, value)}
          onBlur={({ name }): void => form.setFieldTouched(name)}
        />
        {form.touched[FormName.INDUSTRY] && (
          <ErrorMessage message={form.errors[FormName.INDUSTRY]} />
        )}
      </FormField>

      {/* MAU */}
      {enableMau && (
        <FormField>
          <Select
            name={FormName.MAU}
            value={form.values[FormName.MAU]}
            placeholder="Your project's MAU..."
            icon={(
              <StatsSVG />
            )}
            options={mau}
            isErrored={form.touched[FormName.MAU] && !!form.errors[FormName.MAU]}
            isDisabled={form.isSubmitting}
            onChange={({ name, value }): void => form.setFieldValue(name, value)}
            onBlur={({ name }): void => form.setFieldTouched(name)}
          />
          {form.touched[FormName.MAU] && (
            <ErrorMessage message={form.errors[FormName.MAU]} />
          )}
        </FormField>
      )}

      {/* PUSHWOOSH PRIVACY */}
      <FormField $isBigGap>
        <Checkbox
          name={FormName.PUSHWOOSH_PRIVACY}
          value={form.values[FormName.PUSHWOOSH_PRIVACY]}
          isChecked={form.values[FormName.PUSHWOOSH_PRIVACY]}
          isErrored={form.touched[FormName.PUSHWOOSH_PRIVACY] && !!form.errors[FormName.PUSHWOOSH_PRIVACY]}
          isDisabled={form.isSubmitting}
          onChange={({ name, isChecked }): void => form.setFieldValue(name, isChecked)}
          onBlur={({ name }): void => form.setFieldTouched(name)}
        >
          I agree to Pushwoosh
          {' '}
          <Link href="https://www.pushwoosh.com/privacy-policy/" title="Privacy Policy" target="_blank">
            Privacy Policy.
          </Link>
        </Checkbox>
        {form.touched[FormName.PUSHWOOSH_PRIVACY] && (
          <ErrorMessage message={form.errors[FormName.PUSHWOOSH_PRIVACY]} />
        )}
      </FormField>

      {/* WEBINARS PRIVACY */}
      {enablePartnerPrivacy && (
        <FormField $isSmallGap>
          <Checkbox
            name={FormName.PARTNER_PRIVACY}
            value={form.values[FormName.PARTNER_PRIVACY]}
            isChecked={form.values[FormName.PARTNER_PRIVACY]}
            isErrored={form.touched[FormName.PARTNER_PRIVACY] && !!form.errors[FormName.PARTNER_PRIVACY]}
            isDisabled={form.isSubmitting}
            onChange={({ name, isChecked }): void => form.setFieldValue(name, isChecked)}
            onBlur={({ name }): void => form.setFieldTouched(name)}
          >
            I agree to
            {' '}
            {partnerPrivacyLinkList.map((item: any, key: number) => (
              <span key={item.id}>
                <Link
                  href={item.url}
                  title="Privacy Policy"
                  target="_blank"
                >
                  {item.label}
                </Link>
                {partnerPrivacyLinkList.length - key > 1 && (', ')}
              </span>
            ))}
            {' '}
            Privacy Policy.
          </Checkbox>
          {form.touched[FormName.PARTNER_PRIVACY] && (
            <ErrorMessage message={form.errors[FormName.PARTNER_PRIVACY]} />
          )}
        </FormField>
      )}

      {footnoteText && (
        <Footnote>
          {footnoteText}
        </Footnote>
      )}
      <ReCAPTCHA />
      <Button
        type="submit"
        view="primary"
        id="leadFormSubmit"
        isDisabled={form.isSubmitting}
        onClick={onSubmitClick}
      >
        {buttonText}
      </Button>
    </FormBox>
  );
}
